import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import useEntityFetcher from '../../../../io/useEntityFetcher'
import { com } from '@eidu/form'
import { AssetLabel } from './assetReferenceDecoration'
import { ASSET_REFERENCE_PATTERN } from './assetReferencePattern'
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString

function extractUuids(text: string): Set<string> {
  const uuids = new Set<string>()
  let match
  ASSET_REFERENCE_PATTERN.lastIndex = 0
  while ((match = ASSET_REFERENCE_PATTERN.exec(text)) !== null) {
    uuids.add(match[1])
  }
  return uuids
}

const useAssetReferenceLabelResolver = (value: string) => {
  const [resolvedLabels, setResolvedLabels] = useState<Map<string, AssetLabel>>(new Map())
  const resolvedRef = useRef<Set<string>>(new Set())
  const fetchById = useEntityFetcher()

  const resolve = useCallback(
    async (uuids: Set<string>) => {
      const newUuids = [...uuids].filter((id) => !resolvedRef.current.has(id))
      if (newUuids.length === 0) return

      const results = await Promise.allSettled(
        newUuids.map(async (uuid) => {
          const entity = await fetchById(entityIdFromString(uuid))
          return { uuid, entity }
        })
      )

      setResolvedLabels((prev) => {
        const next = new Map(prev)
        for (const result of results) {
          if (result.status === 'fulfilled' && result.value.entity) {
            const { uuid, entity } = result.value
            next.set(uuid, {
              label: entity.primaryLabelText || uuid.slice(0, 8),
              typeId: entity.entity.typeId.toString(),
            })
            resolvedRef.current.add(uuid)
          }
        }
        return next
      })
    },
    [fetchById]
  )

  const debouncedResolve = useDebouncedCallback((text: string) => {
    const uuids = extractUuids(text)
    resolve(uuids)
  }, 300)

  useEffect(() => {
    debouncedResolve(value)
  }, [value, debouncedResolve])

  return { resolvedLabels }
}

export default useAssetReferenceLabelResolver
